import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { validateInviteCode, registerUser } from '../utils/api';
import AuthShell from '../components/AuthShell';
import PasswordInput from '../components/PasswordInput';

export default function RegisterPage() {
  const [step, setStep] = useState(1);
  const [inviteCode, setInviteCode] = useState('');
  const [codeRole, setCodeRole] = useState('');
  const [form, setForm] = useState({ email: '', password: '', confirmPassword: '', firstName: '', lastName: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await validateInviteCode(inviteCode.toUpperCase().trim());
      if (!res.valid) throw new Error(res.error || 'Invalid code');
      setCodeRole(res.role);
      setStep(2);
    } catch (err) {
      setError(err.message || 'Invalid invite code');
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirmPassword) { setError('Passwords do not match'); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      const res = await registerUser({ ...form, inviteCode: inviteCode.toUpperCase().trim() });
      if (res.error) throw new Error(res.error);
      navigate('/login', { state: { message: 'Account created! You can now sign in.' } });
    } catch (err) {
      setError(err.message);
    } finally { setLoading(false); }
  };

  return (
    <AuthShell>
      <div className="login-wordmark">
        <div className="login-wordmark-name">Create Your <em>Account</em></div>
        <div className="login-wordmark-sub">Register with invite code</div>
      </div>

      {error && <div className="login-error-msg">{error}</div>}

      {step === 1 && (
        <form onSubmit={handleCodeSubmit}>
          <div className="login-field">
            <label htmlFor="invite-code">Invite Code</label>
            <input
              id="invite-code"
              type="text"
              value={inviteCode}
              onChange={e => setInviteCode(e.target.value.toUpperCase())}
              placeholder="HCA-XXXX-XXXX"
              required
              style={{ fontFamily: 'monospace', fontSize: 18, letterSpacing: '0.08em' }}
            />
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 6, display: 'block' }}>
              Enter the code provided by your administrator
            </span>
          </div>

          <button
            type="submit"
            className={`login-signin-btn${loading ? ' loading' : ''}`}
            disabled={loading || inviteCode.length < 10}
          >
            <span>{loading ? 'Checking' : 'Verify Code'}</span>
            <span className="login-btn-arrow">{loading ? '…' : '→'}</span>
          </button>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={handleRegister}>
          <div className="login-success-msg" style={{ marginBottom: 18 }}>
            ✓ Invite verified — registering as <strong>{codeRole}</strong>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="login-field" style={{ marginBottom: 0 }}>
              <label htmlFor="first-name">First Name</label>
              <input
                id="first-name"
                type="text"
                value={form.firstName}
                onChange={e => setForm({ ...form, firstName: e.target.value })}
                autoComplete="given-name"
                required
              />
            </div>
            <div className="login-field" style={{ marginBottom: 0 }}>
              <label htmlFor="last-name">Last Name</label>
              <input
                id="last-name"
                type="text"
                value={form.lastName}
                onChange={e => setForm({ ...form, lastName: e.target.value })}
                autoComplete="family-name"
                required
              />
            </div>
          </div>

          <div className="login-field" style={{ marginTop: 18 }}>
            <label htmlFor="reg-email">Email Address</label>
            <input
              id="reg-email"
              type="email"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value.toLowerCase() })}
              autoComplete="email"
              required
            />
          </div>

          <div className="login-field">
            <label htmlFor="reg-password">Password</label>
            <PasswordInput
              id="reg-password"
              wrapperClassName="login-field-wrap"
              toggleClassName="login-show-toggle"
              value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
              placeholder="Min 8 chars, upper, lower, number"
              autoComplete="new-password"
              required
            />
          </div>

          <div className="login-field">
            <label htmlFor="confirm-password">Confirm Password</label>
            <PasswordInput
              id="confirm-password"
              wrapperClassName="login-field-wrap"
              toggleClassName="login-show-toggle"
              value={form.confirmPassword}
              onChange={e => setForm({ ...form, confirmPassword: e.target.value })}
              autoComplete="new-password"
              required
            />
          </div>

          <button
            type="submit"
            className={`login-signin-btn${loading ? ' loading' : ''}`}
            disabled={loading}
          >
            <span>{loading ? 'Creating account' : 'Create Account'}</span>
            <span className="login-btn-arrow">{loading ? '…' : '→'}</span>
          </button>
        </form>
      )}

      <div className="login-register-row">
        <span>Already have an account?&nbsp;</span>
        <Link to="/login">Sign in</Link>
      </div>
    </AuthShell>
  );
}
